import React, { PropsWithChildren, useCallback, useMemo, useRef, useState } from "react";
import {
    AlertButton,
    AlertOptions,
    Platform,
    StyleSheet,
    View,
    AlertType as RNAlertType,
    KeyboardType,
    TextInput,
} from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { useTheme } from "../hooks/use-theme";
import { BodyMediumText, TitleMediumText } from "../components/FormattedText";
import VStack from "../ui_atoms/VStack";
import { getShadow } from "../utils";
import Button, { ButtonMode } from "../components/Button";
import HStack from "../ui_atoms/HStack";
import { useI18n } from "../hooks/i18n-hooks";
import TextField from "../components/TextField";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import { OutsidePressHandler } from "../components/OutsidePressHandler";

const AnimatedVStack = Animated.createAnimatedComponent(VStack);

type SanitizedAlertButton = Omit<AlertButton, "onPress"> & {
    onPress?: (text: string) => void;
};

export type AlertType = {
    title: string;
    message?: string;
    buttons?: AlertButton[];
    options?: AlertOptions;
    canDismiss?: boolean;
};

export type PromptType = {
    title: string;
    message?: string;
    callbackOrButtons?: ((text: string) => void) | AlertButton[];
    type?: RNAlertType;
    defaultValue?: string;
    keyboardType?: KeyboardType;
    canDismiss?: boolean;
};

interface AlertContextInterface {
    setAlert: (alert: AlertType) => void;
    setPrompt: (prompt: PromptType) => void;
}

export const AlertManagerContext = React.createContext<AlertContextInterface>({
    setAlert: () => {},
    setPrompt: () => {},
});

const backgroundOpacityValues = [0, 0.25];
const alertOpacityValues = [0, 1];
const alertScaleValues = [0.95, 1];

const AlertProvider = ({ children }: PropsWithChildren) => {
    const styles = useStyles();
    const i18n = useI18n();
    const [alert, setAlert] = useState<AlertType>();
    const [prompt, setPrompt] = useState<PromptType>();
    const [isAlertRendered, setIsAlertRendered] = useState(false);
    const [promptValue, setPromptValue] = useState<string>();
    const isSmall = useIsScreenSizeMin("small");
    const textFieldRef = useRef<TextInput>(null);

    // Background style
    const backgroundOpacityValue = useSharedValue(backgroundOpacityValues[0]);

    const animatedBackgroundStyles = useAnimatedStyle(() => ({
        opacity: backgroundOpacityValue.value,
    }));

    // Alert style
    const alertOpacityValue = useSharedValue(alertOpacityValues[0]);
    const alertScaleValue = useSharedValue(alertScaleValues[0]);

    const animatedAlertStyles = useAnimatedStyle(() => ({
        opacity: alertOpacityValue.value,
        transform: [{ scale: alertScaleValue.value }],
    }));

    const onChange = useCallback(
        (isOpen: boolean) => {
            backgroundOpacityValue.value = withTiming(backgroundOpacityValues[+isOpen]);
            alertScaleValue.value = withTiming(alertScaleValues[+isOpen]);
            alertOpacityValue.value = withTiming(
                alertOpacityValues[+isOpen],
                undefined,
                (finished) => {
                    if (!isOpen && finished) {
                        setIsAlertRendered(false);
                        setAlert(undefined);
                        setPrompt(undefined);
                        setPromptValue(undefined);
                    }
                },
            );
        },
        [alertOpacityValue, alertScaleValue, backgroundOpacityValue],
    );

    const onClose = useCallback(() => {
        onChange(false);
    }, [onChange]);

    const _setAlert = useCallback(
        (_alert: AlertType) => {
            onChange(true);
            setAlert(_alert);
            setIsAlertRendered(true);
        },
        [onChange],
    );

    const _setPrompt = useCallback(
        (_prompt: PromptType) => {
            onChange(true);
            setPrompt(_prompt);
            setPromptValue(_prompt.defaultValue);
            setIsAlertRendered(true);
            setTimeout(() => {
                textFieldRef.current?.focus();
            }, 500);
        },
        [onChange],
    );

    const onSubmitEditing = useCallback(() => {
        if (typeof prompt?.callbackOrButtons === "function") {
            prompt.callbackOrButtons(promptValue || "");
            onClose();
        } else if (Array.isArray(prompt?.callbackOrButtons)) {
            const firstButtonWithOnpress = prompt.callbackOrButtons.find((b) => b.onPress) as
                | SanitizedAlertButton
                | undefined;
            firstButtonWithOnpress?.onPress?.(promptValue || "");
            onClose();
        }
    }, [onClose, prompt, promptValue]);

    const { title, message, buttons } = useMemo(() => {
        const element = alert || prompt;

        const elementButtons =
            alert?.buttons ||
            (Array.isArray(prompt?.callbackOrButtons) ? prompt.callbackOrButtons : undefined);

        return {
            title: element?.title,
            message: element?.message,
            buttons:
                elementButtons?.reverse() ||
                (prompt
                    ? ([
                          {
                              text: i18n.t("cancel"),
                              style: "cancel",
                          },
                          {
                              text: i18n.t("save"),
                              isPreferred: true,
                              onPress: () => {
                                  if (typeof prompt.callbackOrButtons === "function") {
                                      prompt.callbackOrButtons(promptValue || "");
                                  }
                              },
                          },
                      ] as AlertButton[])
                    : [
                          {
                              text: i18n.t("ok"),
                          },
                      ]),
        };
    }, [alert, i18n, prompt, promptValue]);

    const ButtonsContainerComponent = useMemo(
        () => (buttons.length <= 2 && isSmall ? HStack : VStack),
        [buttons.length, isSmall],
    );

    if (Platform.OS !== "web") {
        return children;
    }

    return (
        <AlertManagerContext.Provider value={{ setAlert: _setAlert, setPrompt: _setPrompt }}>
            {children}
            {isAlertRendered && (
                <View style={styles.wrapper}>
                    <Animated.View style={[styles.background, animatedBackgroundStyles]} />
                    <OutsidePressHandler
                        onOutsidePress={onClose}
                        disabled={!(alert?.canDismiss ?? prompt?.canDismiss ?? true)}
                    >
                        <AnimatedVStack gap="2/3" style={[styles.alert, animatedAlertStyles]}>
                            <TitleMediumText>{title}</TitleMediumText>
                            {message && (
                                <BodyMediumText style={styles.message}>{message}</BodyMediumText>
                            )}
                            {prompt && prompt.type !== "default" && (
                                <TextField
                                    autoFocus
                                    ref={textFieldRef}
                                    style={styles.textField}
                                    secureTextEntry={
                                        prompt.type &&
                                        ["login-password", "secure-text"].includes(prompt.type)
                                    }
                                    keyboardType={prompt.keyboardType}
                                    value={promptValue}
                                    onChangeText={setPromptValue}
                                    onSubmitEditing={onSubmitEditing}
                                />
                            )}
                            <ButtonsContainerComponent gap="1/3" style={styles.alertFooter}>
                                {buttons.map((button, index) => {
                                    const buttonMode: ButtonMode = (() => {
                                        switch (button.style) {
                                            case "cancel":
                                                return "outlined";
                                            case "destructive":
                                                return "danger";
                                            case "default":
                                                return "primary";
                                            default: {
                                                if (button.isPreferred) {
                                                    return "secondary";
                                                }
                                                return "primary";
                                            }
                                        }
                                    })();

                                    return (
                                        <Button
                                            key={index}
                                            textSize="small"
                                            adjustsTextFontSizeToFit={true}
                                            buttonMode={buttonMode}
                                            label={button.text || ""}
                                            onPress={() => {
                                                onClose();
                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
                                                button.onPress?.(promptValue as any);
                                            }}
                                        />
                                    );
                                })}
                            </ButtonsContainerComponent>
                        </AnimatedVStack>
                    </OutsidePressHandler>
                </View>
            )}
        </AlertManagerContext.Provider>
    );
};

const useStyles = () => {
    const { colors, gridSize, roundness } = useTheme();

    return StyleSheet.create({
        wrapper: {
            position: "absolute",
            height: "100%",
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
        },
        background: {
            height: "100%",
            width: "100%",
            backgroundColor: "black",
            position: "absolute",
        },
        message: {
            textAlign: "center",
        },
        alert: {
            zIndex: 10000,
            margin: gridSize / 2,
            borderRadius: roundness,
            padding: gridSize,
            backgroundColor: colors.background,
            alignItems: "center",
            maxWidth: 600,
            ...getShadow({ level: 16, shadowColor: colors.darkGrey }),
        },
        textField: {
            width: "100%",
        },
        alertFooter: {
            justifyContent: "flex-end",
        },
    });
};

export default AlertProvider;
